// Button position constants and utilities for Apply button tracking

/**
 * Standard button positions for tracking and analytics
 */
export const BUTTON_POSITIONS = {
  HEADER: 'header',
  FLOORPLANS: 'floorplans', 
  UNITS: 'units',
  MAP: 'map',
  CTA: 'cta',
  UNKNOWN: 'unknown'
};

/**
 * Component name to position mapping for auto-detection
 */
export const COMPONENT_POSITION_MAP = {
  'communityMenu': BUTTON_POSITIONS.HEADER,
  'CommunityMenu': BUTTON_POSITIONS.HEADER,
  'floorplansDisplay': BUTTON_POSITIONS.FLOORPLANS,
  'FloorplansDisplay': BUTTON_POSITIONS.FLOORPLANS,
  'unitDisplayCards': BUTTON_POSITIONS.UNITS,
  'UnitDisplayCards': BUTTON_POSITIONS.UNITS,
  'gmaps': BUTTON_POSITIONS.MAP,
  'GMaps': BUTTON_POSITIONS.MAP,
  'actionItems': BUTTON_POSITIONS.CTA,
  'ActionItems': BUTTON_POSITIONS.CTA
};

/**
 * Valid button positions for validation
 */
export const VALID_POSITIONS = Object.values(BUTTON_POSITIONS);

/**
 * Validate button position and warn in development
 * @param {string} position - Position to validate
 * @returns {boolean} - True if valid position
 */
export const validatePosition = (position) => {
  const isValid = VALID_POSITIONS.includes(position);
  
  if (!isValid && process.env.NODE_ENV === 'development') {
    console.warn(`🚨 Invalid button position: "${position}". Valid positions:`, VALID_POSITIONS);
  }
  
  return isValid;
};

/**
 * Get position from component name with fallback
 * @param {string} componentName - Component name from stack trace
 * @returns {string} - Button position
 */
export const getPositionFromComponent = (componentName) => {
  return COMPONENT_POSITION_MAP[componentName] || BUTTON_POSITIONS.UNKNOWN;
};

/**
 * Auto-detect button position from URL path and DOM context
 * @returns {string} - Detected button position
 */
export const detectButtonPosition = () => {
  try {
    // Method 1: Check URL path for context clues
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      
      // Floorplan pages
      if (currentPath.includes('/floorplans/') || currentPath.includes('/floor-plans/')) {
        console.log('🎯 Detected position: floorplans (from URL path)');
        return BUTTON_POSITIONS.FLOORPLANS;
      }
      
      // Unit pages  
      if (currentPath.includes('/units/') || currentPath.includes('/unit/')) {
        console.log('🎯 Detected position: units (from URL path)');
        return BUTTON_POSITIONS.UNITS;
      }
    }
    
    // Method 2: Check for Apply button context via DOM inspection
    if (typeof document !== 'undefined') {
      // Find the apply button that was just clicked by checking recently active elements
      const activeElement = document.activeElement;
      const recentButton = activeElement && activeElement.textContent && activeElement.textContent.includes('Apply') ? activeElement : null;
      
      if (recentButton) {
        // Check parent containers for context clues
        let parent = recentButton.parentElement;
        let depth = 0;
        
        while (parent && depth < 10) {
          const className = parent.className || '';
          const id = parent.id || '';
          
          // Header/navbar context
          if (className.includes('navbar') || className.includes('header') || id.includes('nav')) {
            console.log('🎯 Detected position: header (from DOM context)');
            return BUTTON_POSITIONS.HEADER;
          }
          
          // Map context
          if (className.includes('map') || id.includes('map') || className.includes('gmaps')) {
            console.log('🎯 Detected position: map (from DOM context)');
            return BUTTON_POSITIONS.MAP;
          }
          
          // Floorplan context
          if (className.includes('floorplan') || className.includes('floor-plan') || id.includes('floorplan')) {
            console.log('🎯 Detected position: floorplans (from DOM context)');
            return BUTTON_POSITIONS.FLOORPLANS;
          }
          
          // Units context  
          if (className.includes('unit') || className.includes('apartment') || id.includes('unit')) {
            console.log('🎯 Detected position: units (from DOM context)');
            return BUTTON_POSITIONS.UNITS;
          }
          
          // CTA/Action context
          if (className.includes('cta') || className.includes('action') || className.includes('hero')) {
            console.log('🎯 Detected position: cta (from DOM context)');
            return BUTTON_POSITIONS.CTA;
          }
          
          parent = parent.parentElement;
          depth++;
        }
      }
    }
    
    // Method 3: Fallback - check current page sections
    if (typeof window !== 'undefined') {
      const url = window.location.href;
      
      // If we're on community page, assume it's a general CTA
      if (url.includes('/apartments/')) {
        console.log('🎯 Detected position: cta (fallback - community page)');
        return BUTTON_POSITIONS.CTA;
      }
    }
    
    console.warn('🤷 Could not auto-detect button position, using unknown');
    return BUTTON_POSITIONS.UNKNOWN;
    
  } catch (error) {
    console.error('Error detecting button position:', error);
    return BUTTON_POSITIONS.UNKNOWN;
  }
};